/* biome-ignore-all lint/suspicious/noExplicitAny: dynamic log data table */
import { Table, Tag, Typography, Button, type TablePaginationConfig, type TableProps } from "antd"
import { EyeOutlined } from "@ant-design/icons"
import type React from "react"
import type { SystemLogItem } from "@/services/log/systemLogApi"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import dayjs from "dayjs"

const { Text } = Typography

interface SystemLogTableProps {
  data: SystemLogItem[]
  loading: boolean
  onViewDetail: (record: SystemLogItem) => void
  pagination?: TableProps["pagination"]
}

export const SystemLogTable: React.FC<SystemLogTableProps> = ({ data, loading, onViewDetail, pagination }) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const getLevelBadge = (level: number) => {
    if (level >= 60)
      return (
        <Tag color="#780650" className="m-0 font-bold">
          FATAL
        </Tag>
      )
    if (level >= 50)
      return (
        <Tag color="error" className="m-0 font-bold">
          ERROR
        </Tag>
      )
    if (level >= 40)
      return (
        <Tag color="warning" className="m-0 font-bold">
          WARN
        </Tag>
      )
    if (level >= 30)
      return (
        <Tag color="processing" className="m-0 font-medium">
          INFO
        </Tag>
      )
    return (
      <Tag color="purple" className="m-0">
        DEBUG
      </Tag>
    )
  }

  const getStatusBadge = (code?: number) => {
    if (!code) return null
    let color = "default"
    if (code >= 500) color = "error"
    else if (code >= 400) color = "warning"
    else if (code >= 200 && code < 300) color = "success"

    return (
      <Tag color={color} className="m-0 font-mono font-semibold text-xs">
        {code}
      </Tag>
    )
  }

  const columns: TableProps<SystemLogItem>["columns"] = [
    {
      title: "Thời gian",
      dataIndex: "time",
      key: "time",
      fixed: "left",
      width: 140,
      align: "center",
      render: (val: string | null) => {
        if (!val) return <Text type="secondary">—</Text>
        const d = dayjs(val)
        return (
          <div className="flex flex-col font-mono text-xs leading-tight">
            <span className="font-medium text-gray-800">{d.format("DD/MM/YYYY")}</span>
            <span className="text-[11px] text-gray-500">{d.format("HH:mm:ss.SSS")}</span>
          </div>
        )
      },
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      width: 85,
      fixed: "left",
      align: "center",
      render: (level: number) => getLevelBadge(level),
    },
    {
      title: "Status",
      key: "statusCode",
      width: 80,
      align: "center",
      render: (_, record: SystemLogItem) => {
        const code = record.err?.statusCode || record.res?.statusCode || record.statusCode
        return getStatusBadge(code) || <Text type="secondary">—</Text>
      },
    },
    {
      title: "Loại lỗi / Tác vụ",
      key: "errType",
      width: 140,
      render: (_, record: SystemLogItem) => {
        const errType = record.err?.type || record.err?.name
        if (errType) {
          return (
            <span className="rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 font-mono font-semibold text-rose-600 text-xs">
              {errType}
            </span>
          )
        }
        if (record.req?.method && record.req?.url) {
          return (
            <span className="font-mono text-blue-600 text-xs">
              <strong className="text-gray-800">{record.req.method}</strong> {record.req.url}
            </span>
          )
        }
        return <Text type="secondary">—</Text>
      },
    },
    {
      title: "Thông điệp (Message)",
      key: "msg",
      render: (_, record: SystemLogItem) => {
        const message = record.err?.message || record.msg || record.raw || "—"
        const isError = (record.level || 0) >= 50
        return (
          <div className={`text-xs ${isError ? "font-medium text-red-700" : "text-gray-800"} line-clamp-2 break-words`}>
            {message}
          </div>
        )
      },
    },
    {
      title: "Thao tác",
      key: "action",
      width: 90,
      fixed: "right",
      align: "center",
      render: (_, record: SystemLogItem) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined className="text-blue-500" />}
          onClick={(e) => {
            e.stopPropagation()
            onViewDetail(record)
          }}
        >
          Chi tiết
        </Button>
      ),
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table<SystemLogItem>
        bordered
        columns={columns}
        dataSource={data}
        rowKey="_id"
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        onRow={(record) => ({
          onClick: () => onViewDetail(record),
          className: "cursor-pointer hover:bg-blue-50/40 transition-colors",
        })}
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

SystemLogTable.displayName = "SystemLogTable"

export default SystemLogTable
